/**
 * Step 3b: Wallet Address Input
 * 
 * User enters a crypto wallet address for direct crypto-to-crypto transfers.
 * Includes real-time validation and network auto-detection.
 * 
 * @module addresses/wizard
 */

import React, { useState, useEffect } from 'react';
import { Heading } from "@/components/ui/typography";
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { NetworkIcon } from '@/components/NetworkIcons';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { validateAddress, getAddressFormat, type Chain } from '@/lib/addr/validate';
import type { BlockchainNetwork, CryptoAsset } from './types';
import { NETWORK_LABELS, ASSET_NETWORK_COMPATIBILITY } from './types';

interface WalletAddressInputProps {
  destinationAddress: string;
  destinationChain: BlockchainNetwork;
  destinationAsset: CryptoAsset;
  onAddressChange: (address: string) => void;
  onChainChange: (chain: BlockchainNetwork) => void;
  onAssetChange: (asset: CryptoAsset) => void;
}

const getChainType = (network: BlockchainNetwork): Chain => {
  switch (network) {
    case 'ethereum':
    case 'polygon':
    case 'arbitrum':
    case 'base':
    case 'optimism':
    case 'avalanche':
      return 'evm';
    case 'solana':
      return 'sol';
    case 'tron':
      return 'tron';
    default:
      return 'evm';
  }
};

const AVAILABLE_NETWORKS: BlockchainNetwork[] = [
  'ethereum', 'polygon', 'arbitrum', 'base', 'solana', 'avalanche', 'optimism', 'tron'
];

const AVAILABLE_ASSETS: CryptoAsset[] = ['USDC', 'USDT', 'ETH'];

export const WalletAddressInput: React.FC<WalletAddressInputProps> = ({
  destinationAddress,
  destinationChain,
  destinationAsset,
  onAddressChange,
  onChainChange,
  onAssetChange,
}) => {
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [validationMessage, setValidationMessage] = useState<string>('');
  const [isPasting, setIsPasting] = useState(false);

  const availableAssetsForChain = AVAILABLE_ASSETS.filter(
    asset => ASSET_NETWORK_COMPATIBILITY[asset]?.includes(destinationChain)
  );

  useEffect(() => {
    if (!destinationAddress) {
      setIsValid(null);
      setValidationMessage('');
      return;
    }

    const chainType = getChainType(destinationChain);
    const valid = validateAddress(chainType, destinationAddress);

    setIsValid(valid);
    if (valid) {
      setValidationMessage('Address is valid');
    } else {
      setValidationMessage(`Invalid ${NETWORK_LABELS[destinationChain]} address format`);
    }
  }, [destinationAddress, destinationChain]);

  useEffect(() => {
    if (!availableAssetsForChain.includes(destinationAsset) && availableAssetsForChain.length > 0) {
      onAssetChange(availableAssetsForChain[0]);
    }
  }, [destinationChain, availableAssetsForChain, destinationAsset, onAssetChange]);

  const handlePaste = async () => {
    try {
      setIsPasting(true);
      const text = await navigator.clipboard.readText();
      onAddressChange(text.trim());
    } catch (err) {
      console.error('Failed to read clipboard:', err);
    } finally {
      setIsPasting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <Heading level={2} className={cn("text-xl sm:text-heading-lg", semanticTokens.fg.default)}>
          Where should we send the crypto?
        </Heading>
        <p className={cn("text-sm sm:text-base", semanticTokens.fg.muted)}>
          Enter your destination wallet address
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className={semanticTokens.fg.muted}>Destination Network</Label>
          <Select value={destinationChain} onValueChange={(v) => onChainChange(v as BlockchainNetwork)}>
            <SelectTrigger>
              <SelectValue>
                <div className="flex items-center gap-2">
                  <NetworkIcon network={destinationChain} className="w-4 h-4" />
                  <span>{NETWORK_LABELS[destinationChain]}</span>
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {AVAILABLE_NETWORKS.map(network => (
                <SelectItem key={network} value={network}>
                  <div className="flex items-center gap-2">
                    <NetworkIcon network={network} className="w-4 h-4" />
                    <span>{NETWORK_LABELS[network]}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className={semanticTokens.fg.muted}>Destination Asset</Label>
          <Select value={destinationAsset} onValueChange={(v) => onAssetChange(v as CryptoAsset)}>
            <SelectTrigger>
              <SelectValue>
                <div className="flex items-center gap-2">
                  <CurrencyIcon symbol={destinationAsset} size="sm" />
                  <span>{destinationAsset}</span>
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {availableAssetsForChain.map(asset => (
                <SelectItem key={asset} value={asset}>
                  <div className="flex items-center gap-2">
                    <CurrencyIcon symbol={asset} size="sm" />
                    <span>{asset}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="destination-address" className={semanticTokens.fg.muted}>
          Wallet Address
        </Label>
        <div className="relative">
          <Input
            id="destination-address"
            value={destinationAddress}
            onChange={(e) => onAddressChange(e.target.value)}
            placeholder={getAddressFormat(getChainType(destinationChain))}
            className={cn(
              "pr-24 font-mono text-sm",
              isValid === true && cn(semanticTokens.positive.border, "focus-visible:ring-2 focus-visible:ring-emerald-500"),
              isValid === false && cn(semanticTokens.danger.border, "focus-visible:ring-2 focus-visible:ring-destructive")
            )}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handlePaste}
            disabled={isPasting}
            className="absolute right-1 top-1/2 -translate-y-1/2 gap-1.5 h-8"
          >
            <SFIcon icon={sfIcons.copy} size="sm" />
            Paste
          </Button>
        </div>
        
        {validationMessage && (
          <div className={cn(
            "flex items-center gap-2 text-sm",
            isValid ? semanticTokens.positive.fg : semanticTokens.danger.fg
          )}>
            {isValid ? <SFIcon icon={sfIcons.check} size="sm" /> : <SFIcon icon={sfIcons.alertCircle} size="sm" />}
            {validationMessage}
          </div>
        )}
        
        <p className={cn("text-xs", semanticTokens.fg.subtle)}>
          Expected format: {getAddressFormat(getChainType(destinationChain))}
        </p>
      </div>

      {destinationAddress && isValid && (
        <div className={cn(
          "p-4 rounded-lg border",
          semanticTokens.positive.bg,
          semanticTokens.positive.border
        )}>
          <div className="flex items-center gap-3">
            <div className={cn("p-2 rounded-lg", semanticTokens.positive.bg)}>
              <SFIcon icon={sfIcons.wallet} size="md" className={semanticTokens.positive.fg} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={cn("text-sm font-medium", semanticTokens.fg.default)}>
                Crypto will be sent to:
              </p>
              <p className={cn("text-xs font-mono truncate", semanticTokens.fg.muted)}>
                {destinationAddress}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <NetworkIcon network={destinationChain} className="w-3 h-3" />
                <span className={cn("text-xs", semanticTokens.fg.muted)}>
                  {destinationAsset} on {NETWORK_LABELS[destinationChain]}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={cn(
        "p-4 rounded-lg border",
        semanticTokens.warning.bg,
        semanticTokens.warning.border
      )}>
        <div className="flex items-start gap-3">
          <SFIcon icon={sfIcons.alertCircle} size="md" className={cn("shrink-0 mt-0.5", semanticTokens.warning.fg)} />
          <div className="space-y-1">
            <p className={cn("text-sm font-medium", semanticTokens.warning.fg)}>
              Double-check the address and network
            </p>
            <p className={cn("text-sm", semanticTokens.fg.muted)}>
              Sending to the wrong address or network can result in permanent loss of funds.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletAddressInput;

